import { motion } from "framer-motion";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import moment from "moment";
import {
    MdHistory,
    MdRefresh,
    MdCalendarToday,
    MdCheckCircle,
    MdSchedule,
    MdCancel,
    MdInfoOutline,
} from "react-icons/md";
import { BASE_API_URL } from "../helpers/Config";
import { getPageStyles, MOTION } from "../helpers/pageTheme";

const HERO_GRADIENT =
    "linear-gradient(135deg, #059669 0%, #047857 55%, #065f46 100%)";

const PAGE_LIMIT = 10;

const LEVEL_FILTERS = [
    { value: "all", label: "All" },
    { value: "0", label: "Self" },
    ...Array.from({ length: 10 }, (_, i) => ({
        value: String(i + 1),
        label: `L${i + 1}`,
    })),
];

const defaultMeta = {
    total: 0,
    limit: PAGE_LIMIT,
    page_no: 1,
    count: 0,
    has_more: false,
    total_page: 0,
};

const getApiError = (payload) => {
    if (!payload) return "Invalid response";
    if (payload.error === "session expired") return "session expired";
    if (payload.error === false || payload.error === null || payload.error === undefined) {
        return null;
    }
    if (payload.error === true) return payload?.message || "Request failed";
    if (typeof payload.error === "string") return payload.error;
    return null;
};

const formatLevelLabel = (level) => {
    const n = Number(level);
    if (n === 0) return "Self";
    if (!isNaN(n)) return `Level ${n}`;
    return "—";
};

const StatusPill = ({ claimed, disqualified, isDark }) => {
    if (disqualified) {
        return (
            <span
                className="d-inline-flex align-items-center gap-1 rounded-pill"
                style={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    padding: "4px 10px",
                    lineHeight: 1.2,
                    backgroundColor: isDark
                        ? "rgba(239, 68, 68, 0.2)"
                        : "#fef2f2",
                    color: isDark ? "#f87171" : "#dc2626",
                    border: `1px solid ${isDark ? "rgba(239, 68, 68, 0.45)" : "#fecaca"
                        }`,
                }}
            >
                <MdCancel size={14} />
                Disqualified
            </span>
        );
    }

    return (
        <span
            className="d-inline-flex align-items-center gap-1 rounded-pill"
            style={{
                fontSize: "0.68rem",
                fontWeight: 700,
                padding: "4px 10px",
                lineHeight: 1.2,
                backgroundColor: claimed
                    ? isDark
                        ? "rgba(34, 197, 94, 0.18)"
                        : "#ecfdf5"
                    : isDark
                        ? "rgba(245, 158, 11, 0.18)"
                        : "#fffbeb",
                color: claimed
                    ? isDark
                        ? "#4ade80"
                        : "#15803d"
                    : isDark
                        ? "#fcd34d"
                        : "#b45309",
                border: `1px solid ${claimed
                    ? isDark
                        ? "rgba(34, 197, 94, 0.35)"
                        : "#bbf7d0"
                    : isDark
                        ? "rgba(245, 158, 11, 0.35)"
                        : "#fde68a"
                    }`,
            }}
        >
            {claimed ? <MdCheckCircle size={14} /> : <MdSchedule size={14} />}
            {claimed ? "Claimed" : "Unclaimed"}
        </span>
    );
};

const CommissionRecordCard = ({
    row,
    isDark,
    styles,
    formatAmount,
    formatLevelLabel,
}) => {
    const isDisqualified = row.status === false;
    const claimed = Boolean(row.is_claimed);
    const levelNum = Number(row.level);
    const isSelf = levelNum === 0;
    const accentColor = isDisqualified
        ? "#ef4444"
        : claimed
            ? "#059669"
            : "#f59e0b";
    const rejectReason =
        typeof row.reject_reason === "string" ? row.reject_reason.trim() : "";
    const totalAmount = Number(row.amount);
    const selfHalf =
        !isNaN(totalAmount) && totalAmount > 0 ? totalAmount / 2 : 0;
    const amountColor = isDisqualified ? "#ef4444" : "#059669";

    return (
        <article
            style={{
                ...styles.card,
                borderLeft: `3px solid ${accentColor}`,
                padding: 0,
            }}
        >
            <div style={{ padding: "14px 16px 12px" }}>
                <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                    <span
                        className="d-inline-block rounded-pill"
                        style={{
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            padding: "5px 12px",
                            letterSpacing: "0.02em",
                            backgroundColor: isSelf
                                ? isDark
                                    ? "rgba(5, 150, 105, 0.2)"
                                    : "#ecfdf5"
                                : isDark
                                    ? "rgba(99, 102, 241, 0.18)"
                                    : "#eef2ff",
                            color: isSelf ? "#34d399" : isDark ? "#a5b4fc" : "#4f46e5",
                            border: `1px solid ${isSelf
                                ? isDark
                                    ? "rgba(52, 211, 153, 0.35)"
                                    : "#a7f3d0"
                                : isDark
                                    ? "rgba(129, 140, 248, 0.35)"
                                    : "#c7d2fe"
                                }`,
                        }}
                    >
                        {formatLevelLabel(row.level)}
                    </span>
                    <StatusPill
                        claimed={claimed}
                        disqualified={isDisqualified}
                        isDark={isDark}
                    />
                </div>

                <div className="d-flex justify-content-between align-items-end gap-3">
                    <div className="min-w-0">
                        <p
                            className="mb-0 text-uppercase"
                            style={{
                                fontSize: "0.62rem",
                                fontWeight: 700,
                                letterSpacing: "0.08em",
                                color: styles.textMuted,
                            }}
                        >
                            {isSelf
                                ? "Commission on principal"
                                : "Commission earned"}
                        </p>
                    </div>
                    <div className="text-end flex-shrink-0">
                        {isSelf ? (
                            <p
                                className="mb-0"
                                style={{
                                    fontSize: "0.82rem",
                                    lineHeight: 1.5,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                <span style={{ color: styles.textMuted }}>
                                    Principal:{" "}
                                </span>
                                <span
                                    className="fw-bold"
                                    style={{ color: amountColor }}
                                >
                                    {formatAmount(selfHalf)}
                                </span>
                                <span
                                    className="mx-1"
                                    style={{ color: styles.textMuted }}
                                >
                                    &
                                </span>
                                <span style={{ color: styles.textMuted }}>
                                    Commission:{" "}
                                </span>
                                <span
                                    className="fw-bold"
                                    style={{ color: amountColor }}
                                >
                                    {formatAmount(selfHalf)}
                                </span>
                            </p>
                        ) : (
                            <p
                                className="mb-0 fw-bold"
                                style={{
                                    fontSize: "1.35rem",
                                    lineHeight: 1.15,
                                    color: amountColor,
                                    fontVariantNumeric: "tabular-nums",
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                {formatAmount(row.amount)}
                            </p>
                        )}
                        <p
                            className="mb-0 mt-1"
                            style={{
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                color: styles.textMuted,
                            }}
                        >
                            Trade from <span style={{ textTransform: "capitalize", fontWeight: 700 }}>{row.crypto || "USDT"}</span>
                        </p>
                    </div>
                </div>

                {isDisqualified && rejectReason && (
                    <div
                        className="d-flex align-items-start gap-2 mt-3 p-2 rounded-2"
                        style={{
                            backgroundColor: isDark
                                ? "rgba(239, 68, 68, 0.12)"
                                : "#fef2f2",
                            border: `1px solid ${isDark ? "rgba(239, 68, 68, 0.35)" : "#fecaca"
                                }`,
                        }}
                    >
                        <MdInfoOutline
                            size={16}
                            className="flex-shrink-0 mt-1"
                            style={{ color: isDark ? "#f87171" : "#dc2626" }}
                        />
                        <p
                            className="mb-0 small"
                            style={{
                                color: isDark ? "#fca5a5" : "#b91c1c",
                                lineHeight: 1.45,
                            }}
                        >
                            {rejectReason}
                        </p>
                    </div>
                )}
            </div>

            <div
                className="d-flex align-items-center gap-2 px-3 py-2"
                style={{
                    borderTop: `1px solid ${isDark ? "#1f2937" : "#f1f5f9"}`,
                    backgroundColor: isDark ? "#0f172a" : "#f8fafc",
                }}
            >
                <MdCalendarToday
                    size={15}
                    style={{ color: styles.textMuted, flexShrink: 0 }}
                />
                <time
                    className="mb-0"
                    style={{
                        fontSize: "0.78rem",
                        fontWeight: 500,
                        color: styles.textMuted,
                    }}
                >
                    {row.create_date
                        ? moment(row.create_date).format("DD MMM YYYY · hh:mm A")
                        : "—"}
                </time>
            </div>
        </article>
    );
};

const RecoveryTradeCommissionHistory = ({ theme = "dark" }) => {
    const [records, setRecords] = useState([]);
    const [meta, setMeta] = useState(defaultMeta);
    const [pageNo, setPageNo] = useState(1);
    const [levelFilter, setLevelFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const navigate = useNavigate();
    const loadMoreRef = useRef(null);
    const fetchIdRef = useRef(0);
    const isDark = theme === "dark";
    const styles = useMemo(() => getPageStyles(isDark), [isDark]);

    const formatAmount = useCallback((amount) => {
        const n = Number(amount);
        if (isNaN(n)) return "$0";
        return `$${n.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        })}`;
    }, []);

    const fetchHistory = useCallback(
        async ({ page = 1, append = false, isRefresh = false, level = levelFilter } = {}) => {
            const token = localStorage.getItem("a5srf4wer7w1cd");
            if (!token) {
                setError("Please login again");
                setLoading(false);
                return;
            }

            const requestId = ++fetchIdRef.current;

            if (append) setLoadingMore(true);
            else if (isRefresh) setRefreshing(true);
            else setLoading(true);
            setError(null);

            const body = {
                token,
                page_no: page,
                limit: PAGE_LIMIT,
            };
            if (level !== "all") {
                body.level = Number(level);
            }

            try {
                const response = await axios.post(
                    `${BASE_API_URL}/recovery-trade/commission-history`,
                    body
                );
                if (requestId !== fetchIdRef.current) return;

                const payload = response?.data;
                const apiError = getApiError(payload);

                if (apiError === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                    return;
                }

                if (apiError) {
                    setError(apiError);
                    toast.error(apiError);
                    return;
                }

                const list = Array.isArray(payload?.data) ? payload.data : [];
                setRecords((prev) => (append ? [...prev, ...list] : list));
                setMeta({ ...defaultMeta, ...(payload?.meta || {}) });
                setPageNo(page);
            } catch (err) {
                if (requestId !== fetchIdRef.current) return;
                const raw =
                    err.response?.data?.message || err.response?.data?.error;
                const errText =
                    typeof raw === "string"
                        ? raw
                        : "Failed to load commission history";
                setError(errText);
                toast.error(errText);
            } finally {
                if (requestId !== fetchIdRef.current) return;
                setLoading(false);
                setLoadingMore(false);
                setRefreshing(false);
            }
        },
        [navigate, levelFilter]
    );

    useEffect(() => {
        fetchHistory({ page: 1, append: false, level: levelFilter });
    }, [levelFilter, fetchHistory]);

    const handleRefresh = () =>
        fetchHistory({ page: 1, append: false, isRefresh: true, level: levelFilter });

    const loadMore = useCallback(() => {
        if (!meta.has_more || loading || loadingMore) return;
        fetchHistory({ page: pageNo + 1, append: true, level: levelFilter });
    }, [meta.has_more, loading, loadingMore, pageNo, levelFilter, fetchHistory]);

    useEffect(() => {
        const sentinel = loadMoreRef.current;
        if (!sentinel || !meta.has_more || loading || loadingMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) loadMore();
            },
            { root: null, rootMargin: "120px", threshold: 0 }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [meta.has_more, loading, loadingMore, loadMore, records.length]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            className="px-3 py-3 overflow-auto"
            style={styles.page}
        >
            <motion.div
                variants={MOTION.container}
                initial="hidden"
                animate="visible"
                className="d-flex flex-column gap-2"
                style={{ maxWidth: 480, margin: "0 auto", paddingBottom: "24px" }}
            >
                <motion.div
                    variants={MOTION.item}
                    style={{ ...styles.hero(HERO_GRADIENT), padding: "12px 14px" }}
                >
                    <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                            <h6
                                className="fw-bold mb-0 d-flex align-items-center gap-1"
                                style={{ fontSize: "0.95rem" }}
                            >
                                <MdHistory size={20} />
                                Commission history
                            </h6>
                            <p className="mb-0 opacity-75" style={{ fontSize: "0.68rem" }}>
                                {meta.total > 0
                                    ? `${meta.total} record${meta.total !== 1 ? "s" : ""}`
                                    : "Recovery trade commission earnings"}
                            </p>
                        </div>
                        <motion.button
                            type="button"
                            className="btn btn-sm d-inline-flex align-items-center justify-content-center flex-shrink-0 border-0 p-0"
                            style={{
                                width: 40,
                                height: 40,
                                background: "rgba(255,255,255,0.2)",
                                border: "1px solid rgba(255,255,255,0.25)",
                                color: "#fff",
                                borderRadius: "10px",
                            }}
                            whileTap={{ scale: 0.96 }}
                            onClick={handleRefresh}
                            disabled={loading || refreshing}
                            aria-label="Refresh"
                        >
                            <motion.span
                                className="d-inline-flex align-items-center justify-content-center"
                                style={{ lineHeight: 0 }}
                                animate={{ rotate: refreshing ? 360 : 0 }}
                                transition={{
                                    duration: 0.8,
                                    repeat: refreshing ? Infinity : 0,
                                    ease: "linear",
                                }}
                            >
                                <MdRefresh size={20} />
                            </motion.span>
                        </motion.button>
                    </div>
                </motion.div>

                <div>
                    <p
                        className="mb-1 px-1"
                        style={{ ...styles.sectionTitle, fontSize: "0.65rem" }}
                    >
                        Filter by level
                    </p>
                    <div
                        className="d-flex gap-1 pb-1"
                        style={{
                            overflowX: "auto",
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                        }}
                    >
                        {LEVEL_FILTERS.map(({ value, label }) => (
                            <motion.button
                                key={value}
                                type="button"
                                className="btn btn-sm flex-shrink-0 border-0"
                                style={styles.filterChip(levelFilter === value)}
                                whileTap={{ scale: 0.97 }}
                                onClick={() => setLevelFilter(value)}
                                disabled={loading && !refreshing}
                            >
                                {label}
                            </motion.button>
                        ))}
                    </div>
                </div>

                {error && !loading && (
                    <div className="p-2 text-center" style={styles.card}>
                        <p className="mb-2 small" style={{ color: styles.textMuted }}>
                            {error}
                        </p>
                        <motion.button
                            type="button"
                            className="btn btn-sm"
                            style={{
                                background:
                                    "linear-gradient(135deg, #059669 0%, #047857 100%)",
                                color: "#fff",
                                fontWeight: 700,
                            }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleRefresh}
                        >
                            Try again
                        </motion.button>
                    </div>
                )}

                {loading && (
                    <div className="text-center py-4">
                        <div
                            className="spinner-border spinner-border-sm text-success"
                            role="status"
                        />
                    </div>
                )}

                {!loading && !error && records.length === 0 && (
                    <div style={styles.empty}>
                        <MdHistory size={36} className="mb-2 opacity-50" />
                        <p className="mb-0 fw-semibold small">No commission records</p>
                    </div>
                )}

                {!loading && !error && records.length > 0 && (
                    <div className="d-flex flex-column gap-3">
                        {records.map((row, index) => (
                            <CommissionRecordCard
                                key={`${row.create_date}-${row.amount}-${index}`}
                                row={row}
                                isDark={isDark}
                                styles={styles}
                                formatAmount={formatAmount}
                                formatLevelLabel={formatLevelLabel}
                            />
                        ))}
                    </div>
                )}

                <div ref={loadMoreRef} style={{ height: 1 }} />

                {loadingMore && (
                    <div className="text-center py-2">
                        <div
                            className="spinner-border spinner-border-sm text-success"
                            role="status"
                        />
                    </div>
                )}

                {!loading && !loadingMore && records.length > 0 && (
                    <p
                        className="text-center mb-0 small"
                        style={{ color: styles.textMuted }}
                    >
                        {meta.has_more
                            ? `Page ${meta.page_no} of ${meta.total_page} · scroll for more`
                            : `Showing all ${records.length} of ${meta.total || records.length}`}
                    </p>
                )}
            </motion.div>
        </motion.div>
    );
};

export default RecoveryTradeCommissionHistory;
